// Face template shapes (docs/face-templates-v1.md). Imports in this folder use explicit `.ts`
// extensions so the Node tools can run the same modules with type stripping.

export type Lod = 'chip' | 'thumb' | 'full' | 'zoom';
export const LODS: Lod[] = ['chip', 'thumb', 'full', 'zoom'];

export type Box = [number, number, number, number];

export type GradientStop = string | [string, number];
export type Paint = string | { linear: GradientStop[]; angle?: number } | { radial: GradientStop[]; at?: [number, number] };

export type TextEffect =
  | { outline: { color: string; width: number } }
  | { shadow: { color: string; dx: number; dy: number; blur: number } }
  | { glow: { color: string; blur: number } }
  | { emboss: { depth: number } }
  | { gradient: { linear: GradientStop[]; angle?: number } };

interface LayerBase {
  id?: string;
  box?: Box;
  opacity?: number;
  blend?: 'normal' | 'multiply' | 'screen' | 'overlay' | 'soft-light';
  rotate?: number;
  mask?: string;
  when?: string;
  unless?: string;
  lod?: Lod[];
  /** Only inside `add`: where to insert, by layer reference. */
  after?: string;
  before?: string;
}

export type Layer = LayerBase & (
  | { type: 'fill'; paint: Paint }
  | { type: 'image'; art?: string; asset?: string; fit?: 'cover' | 'contain' | 'fill' | 'none'; focus?: [number, number] }
  | { type: 'text'; text: string; rich?: string; padding?: [number, number, number, number]; lineHeight?: number; role?: string; ink?: Paint; align?: 'start' | 'center' | 'end'; valign?: 'top' | 'middle' | 'bottom'; fit?: 'shrink' | 'clamp' | 'wrap'; style?: 'normal' | 'italic' | 'caps'; effects?: TextEffect[]; /** Multiplies the role size (0.5 to 2). */ scale?: number }
  | { type: 'icon'; icon: string; paint?: Paint }
  | { type: 'icon-row'; source: string; iconSize?: number; align?: 'start' | 'center' | 'end'; gap?: number; style?: 'count-icon' | 'icon-count' | 'repeat' | 'symbols'; numberKeys?: string[]; /** Tint for icons and numbers (default the face ink). */ paint?: Paint }
  | { type: 'badge'; text?: string; icon?: string; shape?: 'circle' | 'shield' | 'banner' | 'hex' | 'star' | 'pill'; paint?: Paint; ink?: Paint; effects?: TextEffect[] }
  | { type: 'frame'; style?: 'classic' | 'ornate' | 'minimal' | 'tarot' | 'notched' | 'double'; paint?: Paint; width?: number }
  | { type: 'shape'; shape: 'rect' | 'circle' | 'hex' | 'diamond' | 'arch' | 'ribbon'; paint?: Paint; stroke?: { paint: Paint; width: number } }
  | { type: 'pattern'; pattern: 'stripes' | 'dots' | 'grid' | 'crosshatch' | 'noise' | 'wood' | 'linen'; paint?: Paint; scale?: number }
  /** Built-in faces only: one presentation-1 template slot, drawn by the v1 card renderer. */
  | { type: 'slot'; slot: string; binding: string }
);

export type LayerType = Layer['type'];

export interface FaceMatch { kind?: string; catalog?: string[]; tags?: string[]; zoneKind?: string; face?: 'up' | 'down' }

export interface FaceDef {
  id: string;
  size?: { w: number; h: number; radius?: number };
  match?: FaceMatch;
  layers?: Layer[];
  extends?: string;
  add?: Layer[];
  replace?: Record<string, Layer>;
  remove?: string[];
  back?: Layer[];
  lod?: Partial<Record<Lod, { layers?: string[]; size?: { w: number; h: number; radius?: number } }>>;
}

export interface FacesFile { faces: FaceDef[] }

// ---- Atlases and art (face templates spec section 3) -----------------------------------------

export interface AtlasFrame { x: number; y: number; w: number; h: number; pivot?: [number, number] }

export interface AtlasDef {
  id: string;
  /** A manifest asset id (the sheet). */
  asset: string;
  frames?: Record<string, AtlasFrame>;
  grid?: { cell: number | { w: number; h: number }; columns: number };
  names?: string[];
}

export interface AtlasesFile { atlases: AtlasDef[] }

/** Per-slot values: a frame reference `atlas/frame`, `none`, or `pattern:<name>`. */
export interface ArtRule { match: { kind?: string; tags?: string[]; catalog?: string[] }; focus?: Record<string, [number, number]>; [slot: string]: unknown }

export interface ArtFile { slots: string[]; assign: ArtRule[]; fallback?: Record<string, string> }
